/**
 * Error Classification Utilities
 * 
 * Provides service-based error classification for Mesa's three Sentry projects:
 * - API (mesa-api): Tagged by service (review, pull, comment, etc.)
 * - Webhooks (lambda): Tagged by webhook event type
 * - Frontend (mesa-frontend): Tagged by feature area
 * 
 * Classification Strategy:
 * - Custom 'severity' tag based on user impact (critical/high/medium/low)
 * - Service/feature tagged based on error context
 * - Only truly critical errors elevated to 'fatal' level
 * 
 * Note: Sentry's issue.priority field cannot be set programmatically,
 * so we use a custom severity tag for filtering and alerting.
 */

// Error severity levels based on user impact
export type ErrorSeverity = 'critical' | 'high' | 'medium' | 'low';

// Service-based classification for API errors
export type ApiService = 'review' | 'pull' | 'comment' | 'upload' | 'auth' | 'database' | 'github_api' | 'unknown';

// Webhook event types
export type WebhookEvent = 'pull_request' | 'issue_comment' | 'review' | 'check_suite' | 'installation' | 'unknown';

// Frontend features
export type FrontendFeature = 'pulls' | 'reviews' | 'comments' | 'auth' | 'settings' | 'unknown';

export interface ErrorClassification {
  severity: ErrorSeverity;
}


// Simplified error classification based on user impact
export function classifyError(error: any): ErrorClassification {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name || '';
  
  let severity: ErrorSeverity = 'medium'; // Default to medium
  
  // Critical: Service completely unavailable, affects all users
  if (
    error.status >= 500 ||
    errorMessage.includes('database connection') ||
    errorMessage.includes('missing clerk secret') ||
    errorMessage.includes('corrupted sha') ||
    errorMessage.includes('corruption detected') ||
    (errorMessage.includes('auth') && errorMessage.includes('initialization'))
  ) {
    severity = 'critical';
  }
  // High: Major feature broken, affects many users
  else if (
    errorMessage.includes('error reviewing pull request') ||
    errorMessage.includes('error generating pr description') ||
    errorMessage.includes('failed to generate') ||
    errorMessage.includes('failed to merge') ||
    errorMessage.includes('github token expired') ||
    error.status === 503
  ) {
    severity = 'high';
  }
  // Low: Expected user errors, minimal impact
  else if (
    error.status === 404 ||
    error.status === 400 ||
    error.status === 401 ||
    error.status === 403 ||
    errorMessage.includes('validation') ||
    errorMessage.includes('invalid input') ||
    errorMessage.includes('already exists') ||
    errorMessage.includes('duplicate') ||
    errorMessage.includes('not found') ||
    errorMessage.includes('permission denied') ||
    errorMessage.includes('please reconnect') ||
    errorMessage.includes('unknown emoji') ||
    errorName === 'ChunkLoadError'
  ) {
    severity = 'low';
  }
  // Medium: Everything else - degraded experience but functional
  
  return { severity };
}

// Helper function to infer service from API endpoint
function inferServiceFromEndpoint(endpoint: string): ApiService {
  if (endpoint.includes('/review')) return 'review';
  if (endpoint.includes('/pulls')) return 'pull';
  if (endpoint.includes('/comments')) return 'comment';
  if (endpoint.includes('/upload')) return 'upload';
  if (endpoint.includes('/auth')) return 'auth';
  if (endpoint.includes('/github')) return 'github_api';
  return 'unknown';
}

// Helper function to infer frontend feature from URL
function inferFeatureFromUrl(url: string): FrontendFeature {
  if (url.includes('/pulls')) return 'pulls';
  if (url.includes('/reviews')) return 'reviews';
  if (url.includes('/comments')) return 'comments';
  if (url.includes('/settings')) return 'settings';
  if (url.includes('/auth') || url.includes('/sign')) return 'auth';
  return 'unknown';
}

// API beforeSend handler
export function createApiBeforeSendHandler() {
  return (event: any, hint: any) => {
    if (event.exception?.values?.[0]) {
      const error = hint.originalException;
      const classification = classifyError(error);
      
      // Ensure tags exist
      if (!event.tags) {
        event.tags = {};
      }
      
      // Determine service from explicit tag or context
      let service: ApiService = 'unknown';
      if (event.tags?.service) {
        service = event.tags.service;
      } else if (event.tags?.operation) {
        // Map operation to service
        if (event.tags.operation.includes('review')) service = 'review';
        else if (event.tags.operation.includes('pull')) service = 'pull';
        else if (event.tags.operation.includes('comment')) service = 'comment';
        else if (event.tags.operation.includes('upload')) service = 'upload';
      } else if (event.extra?.endpoint) {
        service = inferServiceFromEndpoint(event.extra.endpoint);
      } else if (error?.__sentryContext?.endpoint) {
        service = inferServiceFromEndpoint(error.__sentryContext.endpoint);
      }
      
      event.tags = {
        ...event.tags,
        severity: classification.severity,
        service,
        project: 'api'
      };
      
      // Only set level to 'fatal' for truly critical errors
      if (classification.severity === 'critical') {
        event.level = 'fatal';
      }
    }
    
    return event;
  };
}

// Webhook beforeSend handler
export function createWebhookBeforeSendHandler() {
  return (event: any, hint: any) => {
    if (event.exception?.values?.[0]) {
      const error = hint.originalException;
      const classification = classifyError(error);
      
      // Ensure tags exist
      if (!event.tags) {
        event.tags = {};
      }
      
      // Webhook type is usually explicitly tagged
      const webhookEvent = event.tags?.webhook_type || event.tags?.webhook_event || 'unknown';
      
      event.tags = {
        ...event.tags,
        severity: classification.severity,
        webhook_event: webhookEvent,
        project: 'webhook'
      };
      
      // Only set level to 'fatal' for truly critical errors
      if (classification.severity === 'critical') {
        event.level = 'fatal';
      }
    }
    
    return event;
  };
}

// Frontend beforeSend handler
export function createFrontendBeforeSendHandler() {
  return (event: any, hint: any) => {
    if (event.exception?.values?.[0]) {
      const error = hint.originalException;
      const classification = classifyError(error);
      
      // Ensure tags exist
      if (!event.tags) {
        event.tags = {};
      }
      
      // Infer feature from URL or explicit tag
      let feature: FrontendFeature = 'unknown';
      if (event.tags?.feature) {
        feature = event.tags.feature;
      } else if (typeof window !== 'undefined' && window.location) {
        feature = inferFeatureFromUrl(window.location.pathname);
      }
      
      event.tags = {
        ...event.tags,
        severity: classification.severity,
        feature,
        project: 'frontend'
      };
      
      // Only set level to 'fatal' for truly critical errors
      if (classification.severity === 'critical') {
        event.level = 'fatal';
      }
    }
    
    return event;
  };
}